import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationService } from '../services/organizationService';
import { customerService } from '../services/customerService';
import { integrationService } from '../services/integrationService';
import { customerCategoriesService } from '../services/customerCategoriesService';
import { governanceOverlaysService } from '../services/governanceOverlaysService';
import { useUser } from '../context/UserContext';
import { TitleContext } from '../components/Topbar';
import ToastNotification from '../components/ToastNotification';
import { APArchetype } from '../types/organizations';

// Import step components
import AuthorizeXeroStep from '../components/onboarding/AuthorizeXeroStep';
import AskContextualizeQuestionsStep from '../components/onboarding/AskContextualizeQuestionsStep';
import CreditControllingPoliciesStep from '../components/onboarding/CreditControllingPoliciesStep';
import UnderstandRubricsStep from '../components/onboarding/UnderstandRubricsStep';
import WeeklyDunningOpStep from '../components/onboarding/WeeklyDunningOpStep';
import OnboardingNavigation from '../components/onboarding/OnboardingNavigation';

interface CustomerInSection {
  id: string;
  customerId: string;
  name: string;
  sectionType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior';
  sectionId: string;
  isDuplicated: boolean;
}

interface OnboardingDetailsProps { }

const OnboardingDetails: React.FC<OnboardingDetailsProps> = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const { setDynamicTitle } = useContext(TitleContext);

  // State management
  const [onboardingStatus, setOnboardingStatus] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [customersInSections, setCustomersInSections] = useState<CustomerInSection[]>([]);
  const [customerCategories, setCustomerCategories] = useState<any[]>([]);
  const [apArchetypes, setApArchetypes] = useState<APArchetype[]>([]);
  const [governanceOverlays, setGovernanceOverlays] = useState<Array<{ id: string; name: string; description?: string }>>([]);
  const [customBehaviors, setCustomBehaviors] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [customerNotesCount, setCustomerNotesCount] = useState<Record<string, number>>({});
  const [selectedStep, setSelectedStep] = useState<'authorize' | 'contextualize' | 'policies' | 'rubrics' | 'upcoming-dunning'>('authorize');
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  // Handle customer note count changes
  const handleCustomerNoteCountChange = (customerId: string, delta: number) => {
    setCustomerNotesCount(prev => ({
      ...prev,
      [customerId]: (prev[customerId] || 0) + delta
    }));
  };

  // Function to update customer state from child components
  const updateCustomer = (updatedCustomer: any) => {
    setCustomers(prevCustomers => 
      prevCustomers.map(customer => 
        customer.id === updatedCustomer.id ? updatedCustomer : customer
      )
    );
  };

  // Function to add a new custom behavior
  const addCustomBehavior = (behavior: { id: string; name: string; description: string }) => {
    setCustomBehaviors(prev => [...prev, behavior]);
  };

  // Function to add a new customer (for duplicating)
  const addCustomer = (newCustomer: any) => {
    setCustomers(prev => [...prev, newCustomer]);
  };

  // Function to remove a customer (for duplicated customers)
  const removeCustomer = (customerId: string) => {
    setCustomers(prev => prev.filter(customer => customer.id !== customerId));
  };

  // Function to update custom behaviors (for future use if needed)
  const updateCustomBehaviors = (behaviors: Array<{ id: string; name: string; description: string }>) => {
    setCustomBehaviors(behaviors);
  };

  // Function to mark a step as completed
  const markStepCompleted = (step: string) => {
    setCompletedSteps(prev => new Set(Array.from(prev).concat(step)));
  };

  // Fetch organization details
  const fetchOrganization = async () => {
    if (!organizationId) return;

    try {
      const response = await organizationService.getOrganization(organizationId!, accessToken!, setUserAndToken);
      setOrganization(response);
    } catch (err: any) {
      console.error('Failed to fetch organization:', err);
      setError(err.message || 'Failed to fetch organization');
    }
  };

  // Fetch onboarding status
  const fetchOnboardingStatus = async () => {
    if (!organizationId) return;

    try {
      const response = await organizationService.getOnboardingStatus(organizationId!, accessToken!, setUserAndToken);
      setOnboardingStatus(response);
    } catch (err: any) {
      console.error('Failed to fetch onboarding status:', err);
      setError(err.message || 'Failed to fetch onboarding status');
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    if (!organizationId) return;

    try {
      const response = await customerService.getAllCustomersForPSE(organizationId!, accessToken!, setUserAndToken);
      setCustomers(response.customers || []);
    } catch (err: any) {
      console.error('Failed to fetch customers:', err);
    }
  };

  // Fetch customer categories
  const fetchCustomerCategories = async () => {
    if (!organizationId) return;

    try {
      const categories = await organizationService.getOrganizationCustomerCategories(organizationId, accessToken!, setUserAndToken);
      setCustomerCategories(categories);
    } catch (err: any) {
      console.error('Failed to fetch customer categories:', err);
    }
  };

  // Fetch AP archetypes for the organization
  const fetchAPArchetypes = async () => {
    if (!organizationId || !accessToken) return;

    try {
      const archetypes = await organizationService.getOrganizationAPArchetypes(organizationId, accessToken, setUserAndToken);
      setApArchetypes(archetypes as APArchetype[]);
    } catch (err: any) {
      console.error('Error fetching AP archetypes:', err);
      // Don't set error state for AP archetypes as it's optional
    }
  };

  // Fetch governance overlays for the organization
  const fetchGovernanceOverlays = async () => {
    if (!organizationId || !accessToken) return;

    try {
      const overlays = await organizationService.getOrganizationGovernanceOverlays(organizationId, accessToken, setUserAndToken);
      setGovernanceOverlays(overlays);
    } catch (err: any) {
      console.error('Error fetching governance overlays:', err);
      // Don't set error state for governance overlays as it's optional
    }
  };

  // Handle Xero authorization
  const handleAuthorizeXero = async () => {
    if (!organizationId) return;

    try {
      setIntegrationLoading(true);
      setToastMsg('Setting up Xero integration...');

      // Get available integrations and find Xero
      const availableIntegrations = await integrationService.getAvailableIntegrations(accessToken!, setUserAndToken);
      const xeroIntegration = availableIntegrations.find(integration => 
        integration.name === 'xero' || integration.name === 'xero_direct'
      );

      if (!xeroIntegration) {
        throw new Error('Xero integration not available');
      }

      // Create organization integration (backend will handle recreate if needed)
      const orgIntegration = await integrationService.createOrganizationIntegration(
        organizationId!,
        xeroIntegration.id,
        xeroIntegration.name,
        accessToken!,
        setUserAndToken
      );

      // Check if we have a valid authorization link
      if (orgIntegration.link_to_authorize) {
        setToastMsg('Opening Xero authorization...');
        // Open the authorization URL in a new window
        window.open(orgIntegration.link_to_authorize, '_blank');
        
        // Refresh onboarding status to reflect the new integration
        await fetchOnboardingStatus();
      } else {
        throw new Error('Failed to generate Xero authorization link. Please try again or contact support.');
      }
    } catch (err: any) {
      console.error('Xero authorization error:', err);
      setToastMsg(err.message || 'Failed to initiate Xero authorization');
    } finally {
      setIntegrationLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      setDynamicTitle('Onboarding Details');

      const loadData = async () => {
        setLoading(true);
        try {
          await Promise.all([
            fetchOrganization(),
            fetchOnboardingStatus(),
            fetchCustomers(),
            fetchCustomerCategories(),
            fetchAPArchetypes(),
            fetchGovernanceOverlays()
          ]);
        } catch (err: any) {
          setError(err.message || 'Failed to load onboarding data');
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }

    // Cleanup: reset title when component unmounts
    return () => {
      setDynamicTitle(null);
    };
  }, [organizationId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading onboarding details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/organizations')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Organizations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/organizations/${organizationId}`)}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Organization
              </button>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              {organization?.legal_entity_name ? `${organization.legal_entity_name} Onboarding Details` : 'Onboarding Details'}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="col-span-12 md:col-span-4 lg:col-span-3">
            <OnboardingNavigation
              selectedStep={selectedStep}
              onStepChange={(step: 'authorize' | 'contextualize' | 'policies' | 'rubrics' | 'upcoming-dunning') => setSelectedStep(step)}
              onboardingStatus={onboardingStatus}
              completedSteps={completedSteps}
              publicMode={false}
            />
          </aside>

          {/* Content */}
          <section className="col-span-12 md:col-span-8 lg:col-span-9">
            {selectedStep === 'authorize' && (
              <AuthorizeXeroStep
                onboardingStatus={onboardingStatus}
                onAuthorizeXero={handleAuthorizeXero}
                loading={integrationLoading}
              />
            )}

            {selectedStep === 'contextualize' && (
              <AskContextualizeQuestionsStep
                onboardingStatus={onboardingStatus}
                organizationId={organizationId}
                governanceOverlays={governanceOverlays}
                apArchetypes={apArchetypes}
                customers={customers}
                customersInSections={customersInSections}
                setCustomersInSections={setCustomersInSections}
                customBehaviors={customBehaviors}
                onUpdateCustomer={updateCustomer}
                onAddCustomBehavior={addCustomBehavior}
                onAddCustomer={addCustomer}
                onRemoveCustomer={removeCustomer}
                onNextStep={() => {
                  markStepCompleted('contextualize');
                  setSelectedStep('policies');
                }}
              />
            )}

            {selectedStep === 'policies' && (
              <CreditControllingPoliciesStep
                onboardingStatus={onboardingStatus}
                onNextStep={() => {
                  markStepCompleted('policies');
                  setSelectedStep('rubrics');
                }}
              />
            )}

            {selectedStep === 'rubrics' && (
              <UnderstandRubricsStep
                organizationId={organizationId!}
                customers={customers}
                customersInSections={customersInSections}
                governanceOverlays={governanceOverlays}
              />
            )}

            {selectedStep === 'upcoming-dunning' && (
              <WeeklyDunningOpStep
                customers={customers}
                organizationId={organizationId!}
                customerCategories={customerCategories}
                organization={organization}
                customersInSections={customersInSections}
                governanceOverlays={governanceOverlays}
                onCustomerNoteCountChange={handleCustomerNoteCountChange}
              />
            )}

            {/* Toast Notification */}
            <ToastNotification toastMsg={toastMsg} setToastMsg={setToastMsg} />
          </section>
        </div>
      </div>
    </div>
  );
};

export default OnboardingDetails;
